'use client';

// Document-row badge surfacing which converter ran (or which path was taken
// when Docling fell back to MarkItDown). Wave 10 of the Docling Premium
// plan; see `internal/docling/converter.go` for the canonical
// `converter_used` values.
//
// On a fallback, `fallbackCategory` (the server's `docling_fallback_category`)
// picks the explanation — only the category's copy, never the raw error — and
// it is reachable by hover, keyboard focus and screen readers alike (the shared
// Tooltip, which the badge aria-describedby's), not a mouse-only `title`.
//
// Retry availability is the server's `docling_retry_expires_at`
// (`isDoclingRetryLive`), never an inference from `converter_used`.

import { timestampMs, type Timestamp } from '@bufbuild/protobuf/wkt';
import { ArrowRight, RotateCcw } from 'lucide-react';
import { useTranslations } from 'next-intl';
import * as React from 'react';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';

interface ConverterBadgeProps {
  /** "docling" | "docling→markitdown" | "markitdown" | "" */
  converterUsed: string;
  /**
   * The server's docling fallback class: "content" | "infra" | "timeout" |
   * "unknown", or "" for a document converted before BE-12 recorded it.
   */
  fallbackCategory?: string;
  /** When true, render a "Retry (free)" link next to the warning badge. */
  retryAvailable: boolean;
  /** Click handler for the Retry link. Required when retryAvailable=true. */
  onRetry?: () => void;
  className?: string;
}

/**
 * True while the server-issued free-retry token is live — strictly before its
 * expiry, so the link never outlives the token.
 */
export function isDoclingRetryLive(expiresAt: Timestamp | undefined, nowMs: number): boolean {
  return expiresAt !== undefined && timestampMs(expiresAt) > nowMs;
}

const badgeBase =
  'inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 text-xs font-medium';

export function ConverterBadge({
  converterUsed,
  fallbackCategory,
  retryAvailable,
  onRetry,
  className,
}: ConverterBadgeProps): React.ReactElement | null {
  const t = useTranslations('documents');
  const reasonId = React.useId();
  if (converterUsed === 'docling') {
    return (
      <span
        title={t('converterBadge.highEffortTooltip')}
        className={cn(
          badgeBase,
          'bg-success/10 text-success border-success/20',
          className,
        )}
      >
        {t('converterBadge.highEffort')}
      </span>
    );
  }

  if (converterUsed === 'docling→markitdown') {
    // Only the category's copy is shown — the raw docling error never is.
    let fallbackTitle: string;
    switch (fallbackCategory) {
      case 'content':
        fallbackTitle = t('converterBadge.fallbackContent');
        break;
      case 'infra':
      case 'timeout':
      case 'unknown':
        fallbackTitle =
          retryAvailable && onRetry
            ? t('converterBadge.fallbackRetryable')
            : t('converterBadge.fallbackNoRetry');
        break;
      default:
        // Legacy rows (no recorded category) keep the original tooltip.
        fallbackTitle = t('converterBadge.fallbackTooltip');
    }
    return (
      <span className={cn('inline-flex items-center gap-2', className)}>
        {/* The reason must reach touch, keyboard and screen-reader users, so
            it is not a `title`: the badge is focusable, the shared Tooltip
            shows on hover AND focus (wrapping, so it fits a phone), and
            aria-describedby points at the tooltip itself so assistive tech
            announces it with the badge — one copy, not a second sr-only one. */}
        <Tooltip content={fallbackTitle} contentId={reasonId} multiline>
          <span
            tabIndex={0}
            aria-describedby={reasonId}
            className={cn(
              badgeBase,
              'bg-warning/10 text-warning border-warning/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            )}
          >
            {t('converterBadge.highEffort')}
            <ArrowRight className="h-3 w-3" aria-hidden="true" />
            {t('converterBadge.fast')}
          </span>
        </Tooltip>
        {retryAvailable && onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
            aria-label={t('converterBadge.retryAria')}
          >
            <RotateCcw className="h-3 w-3" aria-hidden="true" />
            {t('converterBadge.retryFree')}
          </button>
        )}
      </span>
    );
  }

  // converterUsed === "markitdown" or "" → no badge (default path is
  // not worth a visual call-out).
  return null;
}
